import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults for cookie-based authentication
axios.defaults.withCredentials = true;  // CRITICAL: Send/receive httpOnly cookies

// Set default auth header if token exists (backwards compatibility during migration)
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Generic CRUD operations
const createCrud = (resource) => ({
  getAll: (params) => axios.get(`${API}/${resource}`, { params }).then(res => res.data),
  getOne: (id) => axios.get(`${API}/${resource}/${id}`).then(res => res.data),
  create: (data) => axios.post(`${API}/${resource}`, data).then(res => res.data),
  update: (id, data) => axios.put(`${API}/${resource}/${id}`, data).then(res => res.data),
  delete: (id) => axios.delete(`${API}/${resource}/${id}`).then(res => res.data),
});

export const leadsApi = {
  ...createCrud('leads'),
  convert: (id) => axios.post(`${API}/leads/${id}/convert`).then(res => res.data),
};

export const clientsApi = {
  ...createCrud('clients'),
  createPortalAccess: (id) => axios.post(`${API}/clients/${id}/portal-access`).then(res => res.data),
};

export const parksApi = createCrud('parks');
export const contractorsApi = createCrud('contractors');
export const tasksApi = createCrud('tasks');

// Legacy APIs for backward compatibility (these may not have backend endpoints)
export const casesApi = createCrud('cases');
export const reportsApi = createCrud('reports');

// Quotes API with full CRUD and workflow actions
export const quotesApi = {
  ...createCrud('quotes'),
  send: (id) => axios.post(`${API}/quotes/${id}/send`).then(res => res.data),
  accept: (id) => axios.post(`${API}/quotes/${id}/accept`).then(res => res.data),
  reject: (id) => axios.post(`${API}/quotes/${id}/reject`).then(res => res.data),
  getByCase: (caseId) => axios.get(`${API}/quotes`, { params: { case_id: caseId } }).then(res => res.data),
};

// Invoices API with full CRUD and workflow actions
export const invoicesApi = {
  ...createCrud('invoices'),
  issue: (id) => axios.post(`${API}/invoices/${id}/issue`).then(res => res.data),
  markPaid: (id, amount = null) => axios.post(`${API}/invoices/${id}/mark-paid`, null, { params: amount ? { amount } : {} }).then(res => res.data),
  getByCase: (caseId) => axios.get(`${API}/invoices`, { params: { case_id: caseId } }).then(res => res.data),
};

// Contacts API
export const contactsApi = {
  ...createCrud('contacts'),
  getByClient: (clientId) => axios.get(`${API}/contacts`, { params: { client_id: clientId } }).then(res => res.data),
};

// Claims API
export const claimsApi = {
  ...createCrud('claims'),
  getByCase: (caseId) => axios.get(`${API}/claims`, { params: { case_id: caseId } }).then(res => res.data),
};

// Contractor Requests API
export const contractorRequestsApi = {
  getAll: (caseId) => axios.get(`${API}/contractor-requests`, { params: { case_id: caseId } }).then(res => res.data),
  create: (data) => axios.post(`${API}/contractor-requests`, data).then(res => res.data),
  update: (id, data) => axios.put(`${API}/contractor-requests/${id}`, data).then(res => res.data),
};

// Activities API
export const activitiesApi = {
  getAll: (params) => axios.get(`${API}/activities`, { params }).then(res => res.data),
  getRecent: (limit = 50) => axios.get(`${API}/activities/recent`, { params: { limit } }).then(res => res.data),
  getByRecord: (relatedTable, relatedRecordId) => 
    axios.get(`${API}/activities`, { params: { related_table: relatedTable, related_record_id: relatedRecordId } }).then(res => res.data),
};

// File Upload API
export const filesApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },
  uploadMultiple: (files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return axios.post(`${API}/upload/multiple`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },
  getAll: () => axios.get(`${API}/uploaded-files`).then(res => res.data),
  delete: (fileId) => axios.delete(`${API}/files/${fileId}`).then(res => res.data),
  getUrl: (filename) => `${API}/api/files/${filename}`,
};

// Jobs API (new workflow)
export const jobsApi = {
  getAll: (params) => axios.get(`${API}/jobs`, { params }).then(res => res.data),
  getOne: (id) => axios.get(`${API}/jobs/${id}`).then(res => res.data),
  create: (data) => axios.post(`${API}/jobs`, data).then(res => res.data),
  update: (id, data) => axios.put(`${API}/jobs/${id}`, data).then(res => res.data),
  advanceStage: (id, toStage, notes = '') => 
    axios.post(`${API}/jobs/${id}/advance-stage`, null, { params: { to_stage: toStage, notes } }).then(res => res.data),
  assign: (id, surveyorId, templateId) => 
    axios.post(`${API}/jobs/${id}/assign`, null, { params: { surveyor_id: surveyorId, template_id: templateId } }).then(res => res.data),
  assignContractor: (id, contractorId, templateId = null) => 
    axios.post(`${API}/jobs/${id}/assign-contractor`, null, { params: { contractor_id: contractorId, template_id: templateId } }).then(res => res.data),
  schedule: (id, date, time) => 
    axios.post(`${API}/jobs/${id}/schedule`, null, { params: { scheduled_date: date, scheduled_time: time } }).then(res => res.data),
  getActions: (id) => axios.get(`${API}/jobs/${id}/actions`).then(res => res.data),
  addAction: (id, actionType, description, metadata = {}) => 
    axios.post(`${API}/jobs/${id}/action`, metadata, { params: { action_type: actionType, description } }).then(res => res.data),
};

// Staff API
export const staffApi = {
  getAll: () => axios.get(`${API}/staff`).then(res => res.data),
  create: (data) => axios.post(`${API}/staff`, data).then(res => res.data),
  update: (id, data) => axios.put(`${API}/staff/${id}`, data).then(res => res.data),
  getSurveyors: () => axios.get(`${API}/staff/surveyors`).then(res => res.data),
};

// Survey Templates API
export const surveyTemplatesApi = {
  getAll: () => axios.get(`${API}/survey-templates`).then(res => res.data),
  getOne: (id) => axios.get(`${API}/survey-templates/${id}`).then(res => res.data),
  create: (name, surveyType, description = '') => 
    axios.post(`${API}/survey-templates`, null, { params: { name, survey_type: surveyType, description } }).then(res => res.data),
};

// Surveys API (mobile)
export const surveysApi = {
  create: (jobId, templateId) => 
    axios.post(`${API}/surveys`, null, { params: { job_id: jobId, template_id: templateId } }).then(res => res.data),
  getOne: (id) => axios.get(`${API}/surveys/${id}`).then(res => res.data),
  getByJob: (jobId) => axios.get(`${API}/surveys/job/${jobId}`).then(res => res.data),
  update: (id, data) => axios.put(`${API}/surveys/${id}`, data).then(res => res.data),
  updateElement: (id, elementId, data) => axios.put(`${API}/surveys/${id}/element/${elementId}`, data).then(res => res.data),
  complete: (id, summary, recommendations, conclusion, signature) => 
    axios.post(`${API}/surveys/${id}/complete`, null, { 
      params: { summary, recommendations, conclusion, signature_data: signature } 
    }).then(res => res.data),
  updateGps: (id, lat, lng) => 
    axios.post(`${API}/surveys/${id}/gps`, null, { params: { latitude: lat, longitude: lng } }).then(res => res.data),
};

// Damage Descriptions API
export const damageApi = {
  getDescriptions: () => axios.get(`${API}/damage-descriptions`).then(res => res.data),
  getDescription: (damageType, element, severity) => 
    axios.get(`${API}/damage-description/${damageType}/${element}/${severity}`).then(res => res.data),
  getBuildingElements: () => axios.get(`${API}/building-elements`).then(res => res.data),
};

// Policy Wordings API
export const policyWordingsApi = {
  getAll: () => axios.get(`${API}/policy-wordings`).then(res => res.data),
  getOne: (insurerKey) => axios.get(`${API}/policy-wordings/${insurerKey}`).then(res => res.data),
  matchByName: (insurerName) => axios.get(`${API}/policy-wordings/match/${encodeURIComponent(insurerName)}`).then(res => res.data),
};

// Automation API
export const automationApi = {
  checkStaleJobs: () => axios.post(`${API}/emails/check-stale-jobs`).then(res => res.data),
  sendAutoFollowups: () => axios.post(`${API}/emails/send-auto-followups`).then(res => res.data),
  sendFollowup: (jobId) => axios.post(`${API}/emails/send-followup/${jobId}`).then(res => res.data),
};

// Client Portal API
export const portalApi = {
  login: (email, password) => axios.post(`${API}/portal/login`, { email, password }).then(res => res.data),
  changePassword: (newPassword) => axios.post(`${API}/portal/change-password`, null, { params: { new_password: newPassword } }).then(res => res.data),
  getJobs: () => axios.get(`${API}/portal/jobs`).then(res => res.data),
  getReports: () => axios.get(`${API}/portal/reports`).then(res => res.data),
  createRequest: (type, subject, description) => 
    axios.post(`${API}/portal/requests`, null, { params: { request_type: type, subject, description } }).then(res => res.data),
  getRequests: () => axios.get(`${API}/portal/requests`).then(res => res.data),
};

// Client Requests API (admin)
export const clientRequestsApi = {
  getAll: () => axios.get(`${API}/client-requests`).then(res => res.data),
  respond: (id, response) => axios.put(`${API}/client-requests/${id}/respond`, null, { params: { response } }).then(res => res.data),
};

export const dashboardApi = {
  getStats: () => axios.get(`${API}/dashboard/stats`).then(res => {
    // Transform flat API response to nested structure expected by dashboard
    const data = res.data;
    return {
      jobs: {
        total: data.total_jobs || 0,
        active: data.active_jobs || 0,
        by_stage: data.jobs_by_stage || {},
        stale_red: data.stale_jobs?.critical || 0,
        stale_amber: data.stale_jobs?.warning || 0,
      },
      leads: {
        total: data.total_leads || 0,
      },
      clients: {
        total: data.total_clients || 0,
      },
      parks: {
        total: data.total_parks || 0,
      },
      contractors: {
        total: data.total_contractors || 0,
      },
      tasks: {
        pending: data.overdue_tasks || 0,
        overdue: data.overdue_tasks || 0,
      },
      quotes: {
        pending: data.financial?.pending_quotes?.count || 0,
        value_pending: data.financial?.pending_quotes?.value || 0,
        accepted: data.financial?.accepted_quotes?.count || 0,
        value_accepted: data.financial?.accepted_quotes?.value || 0,
      },
      invoices: {
        unpaid: data.financial?.outstanding_invoices?.count || 0,
        value_outstanding: data.financial?.outstanding_invoices?.value || 0,
        paid: data.financial?.paid_invoices?.count || 0,
        value_paid: data.financial?.paid_invoices?.value || 0,
      },
      surveyors: data.surveyors || null,
    };
  }),
  getRecentActivity: () => axios.get(`${API}/dashboard/recent-activity`).then(res => res.data),
  getUnifiedStats: () => axios.get(`${API}/dashboard/unified-stats`).then(res => res.data),
  getAnalytics: () => axios.get(`${API}/analytics/overview`).then(res => res.data),
};

// PDF and Reports API
export const reportsDownloadApi = {
  downloadJobReport: (jobId) => `${API}/jobs/${jobId}/download-report-pdf`,
  downloadQuotePdf: (quoteId) => `${API}/quotes/${quoteId}/download-pdf`,
  getReportData: (jobId) => axios.get(`${API}/jobs/${jobId}/generate-report-data`).then(res => res.data),
};

// System/Scheduler API
export const systemApi = {
  getOverdueJobs: () => axios.get(`${API}/system/overdue-jobs`).then(res => res.data),
  sendReminders: () => axios.post(`${API}/system/send-reminders`).then(res => res.data),
  runOverdueCheck: () => axios.post(`${API}/scheduler/run-overdue-check`).then(res => res.data),
  getSchedulerStatus: () => axios.get(`${API}/scheduler/status`).then(res => res.data),
};

// Unified System APIs
export const unifiedApi = {
  addNote: (jobId, message, noteType, isInternal = false) => 
    axios.post(`${API}/jobs/${jobId}/unified-note`, { message, note_type: noteType, is_internal: isInternal }).then(res => res.data),
  getNotes: (jobId, includeInternal = true) => 
    axios.get(`${API}/jobs/${jobId}/unified-notes`, { params: { include_internal: includeInternal } }).then(res => res.data),
  updateInternalFields: (jobId, data) => 
    axios.patch(`${API}/jobs/${jobId}/internal-fields`, data).then(res => res.data),
  getInternalFields: (jobId) => 
    axios.get(`${API}/jobs/${jobId}/internal-fields`).then(res => res.data),
  assignContractor: (jobId, contractorId) => 
    axios.post(`${API}/jobs/${jobId}/assign-contractor-full`, null, { params: { contractor_id: contractorId } }).then(res => res.data),
  scheduleVisit: (jobId, visitDate, visitTime = null) => 
    axios.patch(`${API}/jobs/${jobId}/schedule-visit`, null, { params: { visit_date: visitDate, visit_time: visitTime } }).then(res => res.data),
  markComplete: (jobId) => 
    axios.post(`${API}/jobs/${jobId}/mark-complete`).then(res => res.data),
  testSync: () => 
    axios.post(`${API}/system/test-sync`).then(res => res.data),
};

export const usersApi = {
  getAll: () => axios.get(`${API}/users`).then(res => res.data),
};

// PPM Core System API
export const ppmCoreApi = {
  // Masters (PPM Library)
  getMasters: (params) => axios.get(`${API}/ppm-core/masters`, { params }).then(res => res.data),
  createMaster: (data) => axios.post(`${API}/ppm-core/masters`, data).then(res => res.data),
  updateMaster: (id, data) => axios.put(`${API}/ppm-core/masters/${id}`, data).then(res => res.data),
  
  // Assets
  getAssets: (params) => axios.get(`${API}/ppm-core/assets`, { params }).then(res => res.data),
  createAsset: (data) => axios.post(`${API}/ppm-core/assets`, data).then(res => res.data),
  updateAsset: (id, data) => axios.put(`${API}/ppm-core/assets/${id}`, data).then(res => res.data),
  
  // Assigned PPM (Schedule)
  getAssigned: (params) => axios.get(`${API}/ppm-core/assigned`, { params }).then(res => res.data),
  assignPPM: (data) => axios.post(`${API}/ppm-core/assign`, data).then(res => res.data),
  updateAssigned: (id, params) => axios.put(`${API}/ppm-core/assigned/${id}`, null, { params }).then(res => res.data),
  
  // Jobs
  getJobs: (params) => axios.get(`${API}/ppm-core/jobs`, { params }).then(res => res.data),
  getJob: (id) => axios.get(`${API}/ppm-core/jobs/${id}`).then(res => res.data),
  createJob: (data) => axios.post(`${API}/ppm-core/jobs`, data).then(res => res.data),
  assignContractor: (id, contractorId, scheduledDate) => 
    axios.put(`${API}/ppm-core/jobs/${id}/assign`, null, { params: { contractor_id: contractorId, scheduled_date: scheduledDate } }).then(res => res.data),
  completeJob: (id, params) => axios.put(`${API}/ppm-core/jobs/${id}/complete`, null, { params }).then(res => res.data),
  updateJobStatus: (id, status, notes) => axios.put(`${API}/ppm-core/jobs/${id}/status`, null, { params: { status, notes } }).then(res => res.data),
  
  // Scheduler
  runScheduler: () => axios.post(`${API}/ppm-core/scheduler/run`).then(res => res.data),
  
  // Dashboard
  getDashboard: () => axios.get(`${API}/ppm-core/dashboard`).then(res => res.data),
  
  // Export
  exportSchedule: (params) => axios.get(`${API}/ppm-core/export/contractor-schedule`, { params }).then(res => res.data),
};

